<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Modal from '../../../../components/Modal.vue';
import Button from '../../../../components-next/button/Button.vue';

const { t } = useI18n();
const emit = defineEmits(['close']);

const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
});

const store = useStore();

const globalWebhookEnabled = ref(false);
const webhookUrl = ref('');
const kanbanTitle = ref('Quadro Kanban');
const selectedTheme = ref('Color');
const testStatus = ref(null);
const testLoading = ref(false);
const eventHistory = ref([]);
const activeTab = ref('preferences');
const showWebhookDetails = ref(false);
const dashboardViewEnabled = ref(true);
const listViewEnabled = ref(true);
const agendaViewEnabled = ref(true);
const backupLoading = ref(false);
const restoreLoading = ref(false);
const lastBackupDate = ref(null);

// Eventos disponíveis para webhook
const availableWebhookEvents = computed(() => [
  {
    id: 'kanban.item.created',
    label: t('KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.ITEM_CREATED.LABEL'),
    description: t(
      'KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.ITEM_CREATED.DESCRIPTION'
    ),
  },
  {
    id: 'kanban.item.updated',
    label: t('KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.ITEM_UPDATED.LABEL'),
    description: t(
      'KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.ITEM_UPDATED.DESCRIPTION'
    ),
  },
  {
    id: 'kanban.item.deleted',
    label: t('KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.ITEM_DELETED.LABEL'),
    description: t(
      'KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.ITEM_DELETED.DESCRIPTION'
    ),
  },
  {
    id: 'kanban.item.stage_changed',
    label: t('KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.STAGE_CHANGED.LABEL'),
    description: t(
      'KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.STAGE_CHANGED.DESCRIPTION'
    ),
  },
  {
    id: 'kanban.items.reordered',
    label: t('KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.ITEMS_REORDERED.LABEL'),
    description: t(
      'KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.ITEMS_REORDERED.DESCRIPTION'
    ),
  },
  {
    id: 'kanban.item.agent.assigned',
    label: t('KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.AGENT_ASSIGNED.LABEL'),
    description: t(
      'KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.AGENT_ASSIGNED.DESCRIPTION'
    ),
  },
  {
    id: 'kanban.item.agent.removed',
    label: t('KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.AGENT_REMOVED.LABEL'),
    description: t(
      'KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.AGENT_REMOVED.DESCRIPTION'
    ),
  },
  {
    id: 'kanban.item.note.created',
    label: t('KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.NOTE_CREATED.LABEL'),
    description: t(
      'KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.NOTE_CREATED.DESCRIPTION'
    ),
  },
  {
    id: 'kanban.item.note.updated',
    label: t('KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.NOTE_UPDATED.LABEL'),
    description: t(
      'KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.NOTE_UPDATED.DESCRIPTION'
    ),
  },
  {
    id: 'kanban.item.note.deleted',
    label: t('KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.NOTE_DELETED.LABEL'),
    description: t(
      'KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.NOTE_DELETED.DESCRIPTION'
    ),
  },
  {
    id: 'kanban.item.checklist.item.created',
    label: t('KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.CHECKLIST_ITEM_CREATED.LABEL'),
    description: t(
      'KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.CHECKLIST_ITEM_CREATED.DESCRIPTION'
    ),
  },
  {
    id: 'kanban.item.checklist.item.updated',
    label: t('KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.CHECKLIST_ITEM_UPDATED.LABEL'),
    description: t(
      'KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.CHECKLIST_ITEM_UPDATED.DESCRIPTION'
    ),
  },
  {
    id: 'kanban.item.checklist.item.deleted',
    label: t('KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.CHECKLIST_ITEM_DELETED.LABEL'),
    description: t(
      'KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.CHECKLIST_ITEM_DELETED.DESCRIPTION'
    ),
  },
  {
    id: 'kanban.item.checklist.item.toggled',
    label: t('KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.CHECKLIST_ITEM_TOGGLED.LABEL'),
    description: t(
      'KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.CHECKLIST_ITEM_TOGGLED.DESCRIPTION'
    ),
  },
  {
    id: 'kanban.item.checklist.item.completed',
    label: t('KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.CHECKLIST_ITEM_COMPLETED.LABEL'),
    description: t(
      'KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.CHECKLIST_ITEM_COMPLETED.DESCRIPTION'
    ),
  },
  {
    id: 'kanban.item.checklist.duplicated',
    label: t('KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.CHECKLIST_DUPLICATED.LABEL'),
    description: t(
      'KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.CHECKLIST_DUPLICATED.DESCRIPTION'
    ),
  },
  {
    id: 'kanban.item.timer.started',
    label: t('KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.TIMER_STARTED.LABEL'),
    description: t(
      'KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.TIMER_STARTED.DESCRIPTION'
    ),
  },
  {
    id: 'kanban.item.timer.stopped',
    label: t('KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.TIMER_STOPPED.LABEL'),
    description: t(
      'KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.TIMER_STOPPED.DESCRIPTION'
    ),
  },
  {
    id: 'kanban.item.status.changed',
    label: t('KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.STATUS_CHANGED.LABEL'),
    description: t(
      'KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.STATUS_CHANGED.DESCRIPTION'
    ),
  },
  {
    id: 'kanban.items.bulk.moved',
    label: t('KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.BULK_MOVED.LABEL'),
    description: t(
      'KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.BULK_MOVED.DESCRIPTION'
    ),
  },
  {
    id: 'kanban.items.bulk.assigned',
    label: t('KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.BULK_ASSIGNED.LABEL'),
    description: t(
      'KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.BULK_ASSIGNED.DESCRIPTION'
    ),
  },
  {
    id: 'kanban.items.bulk.priority_changed',
    label: t('KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.BULK_PRIORITY_CHANGED.LABEL'),
    description: t(
      'KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.BULK_PRIORITY_CHANGED.DESCRIPTION'
    ),
  },
  {
    id: 'kanban.item.checklist.item.agent.assigned',
    label: t('KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.CHECKLIST_ITEM_AGENT_ASSIGNED.LABEL'),
    description: t(
      'KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.CHECKLIST_ITEM_AGENT_ASSIGNED.DESCRIPTION'
    ),
  },
  {
    id: 'kanban.item.checklist.item.agent.removed',
    label: t('KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.CHECKLIST_ITEM_AGENT_REMOVED.LABEL'),
    description: t(
      'KANBAN.SETTINGS.WEBHOOK_EVENTS_LIST.CHECKLIST_ITEM_AGENT_REMOVED.DESCRIPTION'
    ),
  },
]);

const selectedWebhookEvents = ref([
  'kanban.item.created',
  'kanban.item.updated',
  'kanban.item.deleted',
  'kanban.item.stage_changed',
  'kanban.items.reordered',
  'kanban.item.agent.assigned',
  'kanban.item.agent.removed',
  'kanban.item.note.created',
  'kanban.item.checklist.item.toggled',
  'kanban.item.timer.started',
  'kanban.item.timer.stopped',
  'kanban.item.status.changed',
]);

// Computed properties para acessar o store
const webhookUrlFromStore = computed(
  () => store.getters['kanban/getWebhookUrl']
);
const webhookEnabledFromStore = computed(
  () => store.getters['kanban/getWebhookEnabled']
);
const kanbanTitleFromStore = computed(
  () => store.getters['kanban/getKanbanTitle']
);
const dashboardViewEnabledFromStore = computed(
  () => store.getters['kanban/getDashboardViewEnabled']
);
const listViewEnabledFromStore = computed(
  () => store.getters['kanban/getListViewEnabled']
);
const agendaViewEnabledFromStore = computed(
  () => store.getters['kanban/getAgendaViewEnabled']
);

const loadEventHistory = () => {
  // Por enquanto, manter histórico vazio até implementar no backend
  eventHistory.value = [];
};

onMounted(async () => {
  // Carregar configurações do store
  globalWebhookEnabled.value = webhookEnabledFromStore.value;
  webhookUrl.value = webhookUrlFromStore.value;
  kanbanTitle.value = kanbanTitleFromStore.value;
  dashboardViewEnabled.value = dashboardViewEnabledFromStore.value;
  listViewEnabled.value = listViewEnabledFromStore.value;
  agendaViewEnabled.value = agendaViewEnabledFromStore.value;
  
  // Carregar tema do localStorage
  const savedTheme = localStorage.getItem('kanban_theme');
  if (savedTheme) {
    selectedTheme.value = savedTheme;
  }

  // Mock: carregar data do último backup
  const savedBackupDate = localStorage.getItem('kanban_last_backup');
  if (savedBackupDate) {
    lastBackupDate.value = savedBackupDate;
  }
  
  loadEventHistory();
});

// Salvar tema no localStorage quando mudar
watch(selectedTheme, (newTheme) => {
  localStorage.setItem('kanban_theme', newTheme);
});

const handleClose = () => {
  emit('close');
};

const handleSave = async () => {
  try {
    await store.dispatch('kanban/updateWebhookConfig', {
      enabled: globalWebhookEnabled.value,
      webhook_url: webhookUrl.value,
      webhook_events: selectedWebhookEvents.value,
      // Incluir configurações gerais no mesmo payload
      config: {
        title: kanbanTitle.value,
        dashboard_view_enabled: dashboardViewEnabled.value,
        list_view_enabled: listViewEnabled.value,
        agenda_view_enabled: agendaViewEnabled.value,
      },
    });

    handleClose();
  } catch (error) {
    console.error('Erro ao salvar configurações:', error);
    // Aqui você pode adicionar uma notificação de erro
  }
};

const handleTestWebhook = async () => {
  testLoading.value = true;
  testStatus.value = null;

  try {
    const result = await store.dispatch('kanban/testWebhook');
    testStatus.value = {
      success: result.data.success,
      message: result.data.message || result.data.error,
    };
  } catch (error) {
    testStatus.value = {
      success: false,
      message: error.response?.data?.error || 'Erro ao testar webhook',
    };
  }

  testLoading.value = false;
  loadEventHistory();
};

const switchTab = tab => {
  activeTab.value = tab;
};

const toggleWebhookDetails = () => {
  showWebhookDetails.value = !showWebhookDetails.value;
};

const toggleWebhookEvent = eventId => {
  const index = selectedWebhookEvents.value.indexOf(eventId);
  if (index > -1) {
    selectedWebhookEvents.value.splice(index, 1);
  } else {
    selectedWebhookEvents.value.push(eventId);
  }
};

const selectAllEvents = () => {
  selectedWebhookEvents.value = availableWebhookEvents.value.map(
    event => event.id
  );
};

const deselectAllEvents = () => {
  selectedWebhookEvents.value = [];
};

const handleBackup = async () => {
  backupLoading.value = true;
  
  // Mock: simular processo de backup
  await new Promise(resolve => setTimeout(resolve, 1500));
  
  const now = new Date().toLocaleString();
  lastBackupDate.value = now;
  localStorage.setItem('kanban_last_backup', now);
  
  backupLoading.value = false;
};

const handleRestore = async () => {
  if (!confirm(t('KANBAN.SETTINGS.RESTORE_CONFIRM'))) {
    return;
  }
  
  restoreLoading.value = true;
  
  // Mock: simular processo de restauração
  await new Promise(resolve => setTimeout(resolve, 1500));
  
  restoreLoading.value = false;
};
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30"
  >
    <div
      class="bg-white dark:bg-slate-900 rounded-md shadow-lg border border-slate-100 dark:border-slate-800 w-[800px] min-w-[700px] h-[600px] flex flex-col gap-0 p-0"
      style="box-shadow: 0 4px 24px 0 rgba(30, 41, 59, 0.08)"
    >
      <header
        class="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-700"
      >
        <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {{ t('KANBAN.SETTINGS.TITLE') }}
        </h3>
        <button
          @click="handleClose"
          class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
        >
          <svg
            class="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            ></path>
          </svg>
        </button>
      </header>

      <div class="flex flex-1 overflow-hidden">
        <!-- Sidebar com tabs -->
        <div
          class="w-48 border-r border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800"
        >
          <nav class="p-4 space-y-2">
            <button
              @click="switchTab('preferences')"
              :class="[
                'w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors',
                activeTab === 'preferences'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                  : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700',
              ]"
            >
              Preferências
            </button>
            <button
              @click="switchTab('webhook')"
              :class="[
                'w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors',
                activeTab === 'webhook'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                  : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700',
              ]"
            >
              Webhook
            </button>
            <button
              @click="switchTab('backup')"
              :class="[
                'w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors',
                activeTab === 'backup'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                  : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700',
              ]"
            >
              {{ t('KANBAN.SETTINGS.BACKUP_RESTORE') }}
            </button>
          </nav>
        </div>

        <!-- Conteúdo das tabs -->
        <div class="flex-1 p-6 overflow-y-auto">
          <!-- Tab Preferências -->
          <div v-if="activeTab === 'preferences'" class="space-y-6">
            <div>
              <h4
                class="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4"
              >
                {{ t('KANBAN.SETTINGS.GENERAL_SETTINGS') }}
              </h4>

              <div class="space-y-6">
                <!-- Título do Kanban -->
                <div>
                  <label
                    for="kanban-title"
                    class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
                  >
                    {{ t('KANBAN.SETTINGS.KANBAN_TITLE') }}
                  </label>
                  <input
                    v-model="kanbanTitle"
                    id="kanban-title"
                    type="text"
                    class="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:border-slate-600 text-sm"
                    :placeholder="t('KANBAN.SETTINGS.KANBAN_TITLE_PLACEHOLDER')"
                    maxlength="50"
                  />
                  <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {{ t('KANBAN.SETTINGS.KANBAN_TITLE_DESCRIPTION') }}
                  </p>
                </div>

                <!-- Seletor de Tema -->
                <div>
                  <label
                    class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
                  >
                    Tema
                  </label>
                  <div class="flex gap-4">
                    <!-- Card Color -->
                    <button
                      @click="selectedTheme = 'Color'"
                      :style="{
                        borderColor: selectedTheme === 'Color' ? '#8b5cf6' : '#e2e8f0',
                        backgroundColor: selectedTheme === 'Color' ? '#f3e8ff' : 'transparent',
                      }"
                      class="relative flex-1 p-4 rounded-lg border-2 transition-all cursor-pointer dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
                    >
                      <div
                        v-if="selectedTheme === 'Color'"
                        class="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center"
                        style="background-color: #8b5cf6"
                      >
                        <svg
                          class="w-4 h-4 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="3"
                            d="M5 13l4 4L19 7"
                          ></path>
                        </svg>
                      </div>
                      <div
                        class="w-full h-20 rounded flex items-center justify-center"
                        style="background-color: #8b5cf6"
                      >
                        <span class="text-white font-medium">Color</span>
                      </div>
                    </button>

                    <!-- Card Light -->
                    <button
                      @click="selectedTheme = 'Light'"
                      :style="{
                        borderColor: selectedTheme === 'Light' ? '#8b5cf6' : '#e2e8f0',
                        backgroundColor: selectedTheme === 'Light' ? '#f3e8ff' : 'transparent',
                      }"
                      class="relative flex-1 p-4 rounded-lg border-2 transition-all cursor-pointer dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
                    >
                      <div
                        v-if="selectedTheme === 'Light'"
                        class="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center"
                        style="background-color: #8b5cf6"
                      >
                        <svg
                          class="w-4 h-4 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="3"
                            d="M5 13l4 4L19 7"
                          ></path>
                        </svg>
                      </div>
                      <div class="w-full h-20 rounded flex items-center justify-center bg-slate-50 dark:bg-slate-800">
                        <div class="flex items-center gap-2">
                          <div
                            class="w-4 h-4 rounded-full"
                            style="background-color: #8b5cf6"
                          ></div>
                          <span class="text-slate-700 dark:text-slate-300 font-medium">Light</span>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                <!-- Configurações de Views -->
                <div>
                  <label
                    class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3"
                  >
                    {{ t('KANBAN.SETTINGS.VIEWS_SETTINGS') }}
                  </label>
                  <div class="space-y-3">
                    <!-- Dashboard View -->
                    <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <label
                        for="dashboard-view-enabled"
                        class="flex items-center gap-3 cursor-pointer select-none"
                      >
                        <span
                          class="relative inline-block w-11 align-middle select-none transition duration-200 ease-in"
                        >
                          <input
                            v-model="dashboardViewEnabled"
                            type="checkbox"
                            id="dashboard-view-enabled"
                            class="sr-only peer"
                          />
                          <span
                            class="block w-11 h-7 bg-slate-200 dark:bg-slate-700 rounded-full peer-checked:bg-blue-500 transition"
                          ></span>
                          <span
                            class="dot absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition peer-checked:translate-x-4"
                          ></span>
                        </span>
                        <span
                          class="text-sm font-medium text-slate-700 dark:text-slate-300"
                        >
                          {{ t('KANBAN.SETTINGS.ENABLE_DASHBOARD_VIEW') }}
                        </span>
                      </label>
                      <p
                        class="text-xs text-slate-500 dark:text-slate-400 flex-1 min-w-[12rem] basis-full sm:basis-auto"
                      >
                        {{ t('KANBAN.SETTINGS.DASHBOARD_VIEW_ADMIN_ONLY_HINT') }}
                      </p>
                    </div>

                    <!-- Lista View -->
                    <div class="flex items-center gap-3">
                      <label
                        for="list-view-enabled"
                        class="flex items-center gap-3 cursor-pointer select-none"
                      >
                        <span
                          class="relative inline-block w-11 align-middle select-none transition duration-200 ease-in"
                        >
                          <input
                            v-model="listViewEnabled"
                            type="checkbox"
                            id="list-view-enabled"
                            class="sr-only peer"
                          />
                          <span
                            class="block w-11 h-7 bg-slate-200 dark:bg-slate-700 rounded-full peer-checked:bg-blue-500 transition"
                          ></span>
                          <span
                            class="dot absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition peer-checked:translate-x-4"
                          ></span>
                        </span>
                        <span
                          class="text-sm font-medium text-slate-700 dark:text-slate-300"
                        >
                          {{ t('KANBAN.SETTINGS.ENABLE_LIST_VIEW') }}
                        </span>
                      </label>
                    </div>

                    <!-- Agenda View -->
                    <div class="flex items-center gap-3">
                      <label
                        for="agenda-view-enabled"
                        class="flex items-center gap-3 cursor-pointer select-none"
                      >
                        <span
                          class="relative inline-block w-11 align-middle select-none transition duration-200 ease-in"
                        >
                          <input
                            v-model="agendaViewEnabled"
                            type="checkbox"
                            id="agenda-view-enabled"
                            class="sr-only peer"
                          />
                          <span
                            class="block w-11 h-7 bg-slate-200 dark:bg-slate-700 rounded-full peer-checked:bg-blue-500 transition"
                          ></span>
                          <span
                            class="dot absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition peer-checked:translate-x-4"
                          ></span>
                        </span>
                        <span
                          class="text-sm font-medium text-slate-700 dark:text-slate-300"
                        >
                          {{ t('KANBAN.SETTINGS.ENABLE_AGENDA_VIEW') }}
                        </span>
                      </label>
                    </div>
                  </div>
                  <p class="text-xs text-slate-500 dark:text-slate-400 mt-2">
                    {{ t('KANBAN.SETTINGS.VIEWS_DESCRIPTION') }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Tab Webhook -->
          <div v-if="activeTab === 'webhook'" class="space-y-6">
            <div>
              <h4
                class="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4"
              >
                {{ t('KANBAN.SETTINGS.TITLE') }}
              </h4>

              <div class="space-y-4">
                <div class="flex items-center gap-3">
                  <label
                    for="webhook-enabled"
                    class="flex items-center gap-3 cursor-pointer select-none"
                  >
                    <span
                      class="relative inline-block w-11 align-middle select-none transition duration-200 ease-in"
                    >
                      <input
                        v-model="globalWebhookEnabled"
                        type="checkbox"
                        id="webhook-enabled"
                        class="sr-only peer"
                      />
                      <span
                        class="block w-11 h-7 bg-slate-200 dark:bg-slate-700 rounded-full peer-checked:bg-blue-500 transition"
                      ></span>
                      <span
                        class="dot absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition peer-checked:translate-x-4"
                      ></span>
                    </span>
                    <span
                      class="text-sm font-medium text-slate-700 dark:text-slate-300"
                    >
                      {{ t('KANBAN.SETTINGS.ENABLE_GLOBAL_WEBHOOK') }}
                    </span>
                  </label>
                </div>

                <div v-if="globalWebhookEnabled" class="space-y-4 pl-14">
                  <div>
                    <label
                      class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
                      for="webhook-url"
                    >
                      {{ t('KANBAN.SETTINGS.WEBHOOK_URL') }}
                    </label>
                    <input
                      v-model="webhookUrl"
                      id="webhook-url"
                      type="url"
                      class="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:border-slate-600 text-sm"
                      :placeholder="
                        t('KANBAN.SETTINGS.WEBHOOK_URL_PLACEHOLDER')
                      "
                      required
                    />
                  </div>

                  <!-- Seleção de eventos -->
                  <div>
                    <div class="flex items-center justify-between mb-3">
                      <label
                        class="block text-sm font-medium text-slate-700 dark:text-slate-300"
                      >
                        {{ t('KANBAN.SETTINGS.WEBHOOK_EVENTS') }}
                      </label>
                      <span class="text-xs text-slate-500 dark:text-slate-400">
                        {{ t('KANBAN.SETTINGS.WEBHOOK_EVENTS_DESCRIPTION') }}
                      </span>
                    </div>

                    <!-- Botões de seleção rápida -->
                    <div class="flex gap-2 mb-3">
                      <Button
                        variant="outline"
                        color="slate"
                        size="small"
                        @click="selectAllEvents"
                      >
                        {{ t('KANBAN.SETTINGS.SELECT_ALL_EVENTS') }}
                      </Button>
                      <Button
                        variant="outline"
                        color="slate"
                        size="small"
                        @click="deselectAllEvents"
                      >
                        {{ t('KANBAN.SETTINGS.DESELECT_ALL_EVENTS') }}
                      </Button>
                    </div>

                    <!-- Lista de eventos -->
                    <div class="space-y-2">
                      <div
                        v-for="event in availableWebhookEvents"
                        :key="event.id"
                        class="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
                      >
                        <input
                          :id="event.id"
                          type="checkbox"
                          :checked="selectedWebhookEvents.includes(event.id)"
                          @change="toggleWebhookEvent(event.id)"
                          class="w-4 h-4 text-blue-600 bg-slate-100 border-slate-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-slate-800 focus:ring-2"
                        />
                        <div class="flex-1">
                          <label
                            :for="event.id"
                            class="block text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer"
                          >
                            {{ event.label }}
                          </label>
                        </div>
                        <div
                          class="text-xs text-slate-500 dark:text-slate-400 text-right max-w-xs"
                        >
                          {{ event.description }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Botão para expandir/colapsar detalhes -->
                  <div class="flex items-center gap-3">
                    <Button
                      :loading="testLoading"
                      variant="outline"
                      color="blue"
                      size="small"
                      @click="handleTestWebhook"
                    >
                      {{ t('KANBAN.SETTINGS.TEST_WEBHOOK') }}
                    </Button>

                    <button
                      @click="toggleWebhookDetails"
                      class="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
                    >
                      <svg
                        :class="[
                          'w-4 h-4 transition-transform',
                          showWebhookDetails ? 'rotate-180' : '',
                        ]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M19 9l-7 7-7-7"
                        ></path>
                      </svg>
                      {{
                        showWebhookDetails
                          ? t('KANBAN.SETTINGS.HIDE_DETAILS')
                          : t('KANBAN.SETTINGS.SHOW_DETAILS')
                      }}
                    </button>
                  </div>

                  <!-- Status do teste -->
                  <div v-if="testStatus" class="pl-4">
                    <span
                      :class="[
                        'inline-flex items-center px-3 py-1 rounded-full text-xs font-medium',
                        testStatus.success
                          ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                          : 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
                      ]"
                    >
                      {{ testStatus.message }}
                    </span>
                  </div>

                  <!-- Detalhes colapsáveis -->
                  <div
                    v-show="showWebhookDetails"
                    class="space-y-3 pl-4 border-l-2 border-slate-200 dark:border-slate-600"
                  >
                    <div>
                      <label
                        class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
                      >
                        {{ t('KANBAN.SETTINGS.WEBHOOK_LOG') }}
                      </label>
                      <div
                        class="max-h-32 overflow-y-auto bg-slate-50 border border-slate-200 dark:bg-slate-800 dark:border-slate-700 rounded-lg p-3 text-xs"
                      >
                        <div
                          v-if="eventHistory.length === 0"
                          class="text-slate-400 dark:text-slate-400 text-center py-4 select-none bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded"
                        >
                          {{ t('KANBAN.SETTINGS.NO_EVENTS') }}
                        </div>
                        <ul v-else class="space-y-1">
                          <li
                            v-for="(event, idx) in eventHistory"
                            :key="idx"
                            class="flex items-center gap-2"
                          >
                            <span
                              :class="[
                                'px-2 py-0.5 rounded text-xs font-medium',
                                event.status === 'success'
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                                  : 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
                              ]"
                            >
                              {{ event.status }}
                            </span>
                            <span
                              class="text-slate-700 dark:text-slate-300 truncate"
                            >
                              {{ event.event }}
                            </span>
                            <span
                              class="text-slate-500 dark:text-slate-400 ml-auto text-xs"
                            >
                              {{ event.timestamp }}
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Tab Backup e Restauração -->
          <div v-if="activeTab === 'backup'" class="space-y-6 relative">
            <div>
              <h4
                class="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4"
              >
                {{ t('KANBAN.SETTINGS.BACKUP_RESTORE') }}
              </h4>

              <div class="space-y-6">
                <!-- Seção de Backup -->
                <div class="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <div class="flex items-center justify-between mb-3">
                    <div>
                      <h5 class="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {{ t('KANBAN.SETTINGS.BACKUP_TITLE') }}
                      </h5>
                      <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        {{ t('KANBAN.SETTINGS.BACKUP_DESCRIPTION') }}
                      </p>
                    </div>
                  </div>
                  
                  <div class="mt-4 space-y-3">
                    <div v-if="lastBackupDate" class="text-sm text-slate-600 dark:text-slate-400">
                      <span class="font-medium">{{ t('KANBAN.SETTINGS.LAST_BACKUP') }}:</span>
                      <span class="ml-2">{{ lastBackupDate }}</span>
                    </div>
                    <div v-else class="text-sm text-slate-500 dark:text-slate-400">
                      {{ t('KANBAN.SETTINGS.NO_BACKUP_YET') }}
                    </div>
                    
                    <Button
                      :loading="backupLoading"
                      variant="solid"
                      color="blue"
                      size="small"
                      @click="handleBackup"
                    >
                      {{ t('KANBAN.SETTINGS.CREATE_BACKUP') }}
                    </Button>
                  </div>
                </div>

                <!-- Seção de Restauração -->
                <div class="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <div class="flex items-center justify-between mb-3">
                    <div>
                      <h5 class="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {{ t('KANBAN.SETTINGS.RESTORE_TITLE') }}
                      </h5>
                      <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        {{ t('KANBAN.SETTINGS.RESTORE_DESCRIPTION') }}
                      </p>
                    </div>
                  </div>
                  
                  <div class="mt-4">
                    <Button
                      :loading="restoreLoading"
                      variant="outline"
                      color="ruby"
                      size="small"
                      @click="handleRestore"
                    >
                      {{ t('KANBAN.SETTINGS.RESTORE_BACKUP') }}
                    </Button>
                  </div>
                </div>

                <!-- Informações -->
                <div class="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div class="flex items-start gap-3">
                    <svg
                      class="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      ></path>
                    </svg>
                    <div class="text-sm text-blue-800 dark:text-blue-300">
                      <p class="font-medium mb-1">{{ t('KANBAN.SETTINGS.BACKUP_INFO_TITLE') }}</p>
                      <p>{{ t('KANBAN.SETTINGS.BACKUP_INFO_DESCRIPTION') }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Overlay "Em Breve" -->
            <div class="absolute inset-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-10 rounded-lg">
              <div class="text-center">
                <div class="mb-4">
                  <svg
                    class="w-16 h-16 mx-auto text-slate-400 dark:text-slate-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                </div>
                <h3 class="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  Em Breve
                </h3>
                <p class="text-sm text-slate-500 dark:text-slate-400">
                  Esta funcionalidade estará disponível em breve
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer
        class="flex justify-end gap-3 px-6 py-4 border-t border-slate-100 dark:border-slate-700"
      >
        <Button variant="ghost" color="slate" size="small" @click="handleClose">
          {{ t('KANBAN.SETTINGS.CANCEL') }}
        </Button>
        <Button variant="solid" color="blue" size="small" @click="handleSave">
          {{ t('KANBAN.SETTINGS.SAVE') }}
        </Button>
      </footer>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// Switch custom
.dot {
  transition: transform 0.2s, background 0.2s;
  background: #fff;
}
.peer:checked ~ .dot {
  transform: translateX(16px);
  background: #fff;
}
.peer:checked ~ .block {
  background: #3b82f6 !important; /* bg-blue-500 */
}
</style>
